/**
 * Pooled IntersectionObservers.
 *
 * One observer instance can watch thousands of elements, so creating one *per tile* is pure
 * waste. At 449 photos the previous approach allocated ~898 observers; this allocates one per
 * distinct rootMargin — two in practice.
 */

type PoolCallback = (entry: IntersectionObserverEntry) => void;

interface Pool {
  observer: IntersectionObserver;
  callbacks: Map<Element, PoolCallback>;
}

const pools = new Map<string, Pool>();

function poolFor(rootMargin: string): Pool {
  const existing = pools.get(rootMargin);
  if (existing) return existing;

  const callbacks = new Map<Element, PoolCallback>();
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) callbacks.get(entry.target)?.(entry);
    },
    { rootMargin }
  );

  const pool: Pool = { observer, callbacks };
  pools.set(rootMargin, pool);
  return pool;
}

/** Watch an element. Returns an unobserve function. */
export function observeWithPool(
  element: Element,
  rootMargin: string,
  callback: PoolCallback
): () => void {
  const pool = poolFor(rootMargin);
  pool.callbacks.set(element, callback);
  pool.observer.observe(element);

  return () => {
    pool.callbacks.delete(element);
    pool.observer.unobserve(element);
    // The observer itself is kept: there are only ever a couple, and churning them on every
    // unmount would undo the point of pooling.
  };
}

/** For the dev perf HUD. */
export function poolStats(): { observers: number; watched: number } {
  let watched = 0;
  for (const pool of pools.values()) watched += pool.callbacks.size;
  return { observers: pools.size, watched };
}
